/**
 * Plain-text formatting shared by the distribution mappers and the mock backend.
 * Ported from the prototype's helpers (fmtMoney, signed, fmtRows, pl, andList) so numbers and
 * dates read the same everywhere. Pure functions only; no React.
 */
import { timezoneOption, zonedParts } from "./distributionTime";

const WEEKDAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WEEKDAY_LONG = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "1 copy", "3 copies". */
export const pluralize = (count: number, one: string, many: string): string =>
  `${count} ${count === 1 ? one : many}`;

const joinList = (items: readonly string[], word: string): string => {
  if (items.length < 2) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} ${word} ${items[items.length - 1]}`;
};

/** "West, East and Central". */
export const andList = (items: readonly string[]): string => joinList(items, "and");

/** "West, East or Central". */
export const orList = (items: readonly string[]): string => joinList(items, "or");

/** Millions of dollars: 14.8 → "$14.8M", 0.8 → "$800K". */
export const formatMoneyMillions = (millions: number): string =>
  millions >= 1 ? `$${millions.toFixed(1)}M` : `$${Math.round(millions * 1000)}K`;

/** A change with its sign and one decimal, using a true minus: "+5.1%", "−0.2 pt". */
export const formatSignedChange = (value: number, unit: string): string => {
  const sign = value > 0 ? "+" : value < 0 ? "−" : "";
  return `${sign}${Math.abs(value).toFixed(1)}${unit}`;
};

/** Fact rows: 1_420_000 → "1.42M", 960_000 → "960K". */
export const formatRowCount = (rows: number): string => {
  if (rows >= 1e6) return `${(rows / 1e6).toFixed(2)}M`;
  if (rows >= 1e3) return `${Math.round(rows / 1e3)}K`;
  return String(rows);
};

/** "08:00" → "8:00 AM"; "13:30" → "1:30 PM". */
export const formatClockTime = (time: string): string => {
  const [h, m] = time.split(":").map(Number);
  const hour = ((h % 24) + 24) % 24;
  return `${hour % 12 || 12}:${String(m || 0).padStart(2, "0")} ${hour < 12 ? "AM" : "PM"}`;
};

const toInstant = (iso: string): number => new Date(iso).getTime();

/** "8:00 AM" for an ISO instant, in `timeZone`. */
export const formatTimeOfDay = (iso: string, timeZone: string): string => {
  const p = zonedParts(toInstant(iso), timeZone);
  return formatClockTime(`${p.hour}:${p.minute}`);
};

/** "Tue, Oct 13", with ", 2027" when the year isn't `currentYear`. */
export const formatShortDate = (iso: string, timeZone: string, currentYear?: number): string => {
  const p = zonedParts(toInstant(iso), timeZone);
  const year = currentYear !== undefined && p.year !== currentYear ? `, ${p.year}` : "";
  return `${WEEKDAY_SHORT[p.weekday]}, ${MONTH_SHORT[p.month - 1]} ${p.day}${year}`;
};

/** "Tue, Oct 20, 2026". */
export const formatLongDate = (iso: string, timeZone: string): string => {
  const p = zonedParts(toInstant(iso), timeZone);
  return `${WEEKDAY_SHORT[p.weekday]}, ${MONTH_SHORT[p.month - 1]} ${p.day}, ${p.year}`;
};

/** "Oct 20", with ", 2027" when the year isn't `currentYear`. */
export const formatMonthDay = (iso: string, timeZone: string, currentYear?: number): string => {
  const p = zonedParts(toInstant(iso), timeZone);
  const year = currentYear !== undefined && p.year !== currentYear ? `, ${p.year}` : "";
  return `${MONTH_SHORT[p.month - 1]} ${p.day}${year}`;
};

/** A calendar date (yyyy-mm-dd) as "Tue, Oct 20, 2026", read in UTC so no zone shifts it. */
export const formatIsoDateLong = (isoDate: string): string =>
  formatLongDate(`${isoDate}T12:00:00Z`, "UTC");

/** A calendar date (yyyy-mm-dd) as "Oct 20". */
export const formatIsoDateMonthDay = (isoDate: string, currentYear?: number): string =>
  formatMonthDay(`${isoDate}T12:00:00Z`, "UTC", currentYear);

/** Weekday names for the contract's numbering (0 = Monday). */
export const weekdayShortName = (weekday: number): string => WEEKDAY_SHORT[weekday] ?? "";
export const weekdayLongName = (weekday: number): string => WEEKDAY_LONG[weekday] ?? "";

/** "Tue, Oct 13 · 8:00 AM PT" — a send as the list and run pages show it. */
export const formatSendMoment = (iso: string, timeZone: string, currentYear?: number): string =>
  `${formatShortDate(iso, timeZone, currentYear)} · ${formatTimeOfDay(iso, timeZone)} ${timezoneOption(timeZone).abbr}`;

/** "3 min 12 s", "45 s", "1 h 2 min". */
export const formatDuration = (milliseconds: number): string => {
  const total = Math.max(0, Math.round(milliseconds / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours) return `${hours} h ${minutes} min`;
  if (minutes) return `${minutes} min ${String(seconds).padStart(2, "0")} s`;
  return `${seconds} s`;
};

/** "2.4 MB", "830 KB" — file sizes on download chips. */
export const formatBytes = (bytes: number): string => {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  if (bytes >= 1_000) return `${Math.round(bytes / 1_000)} KB`;
  return `${bytes} B`;
};

/** Initials for an avatar: "Maya Chen" → "MC". */
export const initialsOf = (name: string): string =>
  (name.trim() || "?")
    .split(/\s+/)
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

/** "west-leadership" style slug, as the CSV template uses for Slack channels. */
export const slugOf = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
